import {inject, Injectable} from '@angular/core';
import {FirebaseError} from 'firebase/app';
import {
	convertMessage as AuthErrorCodeToHumanConvertMessage,
	isPasswordRequirementsError,
	PASSWORD_REQUIREMENTS_MESSAGE
} from '../../helper/AuthErrorCodeToHuman';
import {LoggerService} from './logger.service';
import {ToastService} from './toast.service';

/**
 * Types d'erreurs supportés par le service
 */
export enum ErrorType {
	AUTH = 'auth',
	FIRESTORE = 'firestore',
	STORAGE = 'storage',
	NETWORK = 'network',
	UNKNOWN = 'unknown'
}

/**
 * Interface pour les erreurs personnalisées
 */
export interface AppError {
	code?: string;
	message: string;
	type: ErrorType;
	originalError?: unknown;
}

/**
 * Service centralisé pour la gestion des erreurs dans l'application.
 * 
 * Ce service fournit une interface unifiée pour :
 * - Capturer et logger les erreurs
 * - Traduire les messages d'erreur pour l'utilisateur
 * - Afficher des notifications d'erreur via ToastService
 * - Gérer les erreurs Firebase (Auth, Firestore, Storage)
 * 
 * @example
 * ```typescript
 * try {
 *   await this.authService.login(email, password);
 * } catch (error) {
 *   this.errorHandler.handleError(error);
 * }
 * ```
 */
@Injectable({
	providedIn: 'root'
})
export class ErrorHandlerService {
	private readonly logger = inject(LoggerService);
	private readonly toastService = inject(ToastService);

	/**
	 * Gère une erreur de manière centralisée.
	 * 
	 * @param error - L'erreur à gérer (peut être une Error, FirebaseError, ou AppError)
	 * @param showToast - Si true, affiche un toast à l'utilisateur (défaut: true)
	 * @param logError - Si true, log l'erreur dans la console (défaut: true)
	 * @returns L'erreur formatée sous forme d'AppError
	 */
	handleError(
		error: unknown,
		showToast: boolean = true,
		logError: boolean = true
	): AppError {
		const appError = this.normalizeError(error);

		if (logError) {
			this.logError(appError);
		}

		if (showToast) {
			this.showErrorToUser(appError);
		}

		return appError;
	}

	/**
	 * Normalise une erreur en AppError.
	 * 
	 * @param error - L'erreur à normaliser
	 * @returns Une AppError normalisée
	 */
	private normalizeError(error: unknown): AppError {
		// Erreur Firebase
		if (this.isFirebaseError(error)) {
			return this.handleFirebaseError(error);
		}

		// AppError déjà formatée
		if (this.isAppError(error)) {
			return error;
		}

		// Error standard
		if (error instanceof Error) {
			return {
				message: error.message,
				type: ErrorType.UNKNOWN,
				originalError: error
			};
		}

		// Erreur inconnue
		return {
			message: 'Erreur inconnue',
			type: ErrorType.UNKNOWN,
			originalError: error
		};
	}

	/**
	 * Vérifie si l'erreur est une FirebaseError.
	 */
	private isFirebaseError(error: unknown): error is FirebaseError {
		return (
			typeof error === 'object' &&
			error !== null &&
			'code' in error &&
			'message' in error
		);
	}

	/**
	 * Vérifie si l'erreur est une AppError.
	 */
	private isAppError(error: unknown): error is AppError {
		return (
			typeof error === 'object' &&
			error !== null &&
			'type' in error &&
			'message' in error
		);
	}

	/**
	 * Gère une erreur Firebase spécifique.
	 */
	private handleFirebaseError(error: FirebaseError): AppError {
		const code = String(error.code ?? '');
		let message = error.message;
		let type = ErrorType.UNKNOWN;

		// REST API / Identity Platform: PASSWORD_DOES_NOT_MEET_REQUIREMENTS in message (code may be 400 or generic)
		if (isPasswordRequirementsError(error.message)) {
			type = ErrorType.AUTH;
			message = PASSWORD_REQUIREMENTS_MESSAGE;
			return { code, message, type, originalError: error };
		}

		// Erreur d'authentification
		if (code.startsWith('auth/')) {
			type = ErrorType.AUTH;
			message = AuthErrorCodeToHumanConvertMessage(code);
		}
		// Erreur Firestore
		else if (code.startsWith('firestore/') || code.startsWith('permission-denied')) {
			type = ErrorType.FIRESTORE;
			message = this.translateFirestoreError(code);
		}
		// Erreur Storage
		else if (code.startsWith('storage/')) {
			type = ErrorType.STORAGE;
			message = this.translateStorageError(code);
		}
		// Erreur réseau
		else if (code.includes('network') || code.includes('unavailable')) {
			type = ErrorType.NETWORK;
			message = 'Erreur de connexion réseau. Vérifiez votre connexion internet.';
		}

		return {
			code,
			message,
			type,
			originalError: error
		};
	}

	/**
	 * Traduit une erreur Firestore en message utilisateur.
	 */
	private translateFirestoreError(code: string): string {
		const errorMap: Record<string, string> = {
			'permission-denied': 'Vous n\'avez pas la permission d\'effectuer cette action.',
			'not-found': 'La ressource demandée n\'a pas été trouvée.',
			'already-exists': 'Cette ressource existe déjà.',
			'invalid-argument': 'Les arguments fournis sont invalides.',
			'deadline-exceeded': 'La requête a expiré. Veuillez réessayer.',
			'unauthenticated': 'Vous devez être authentifié pour effectuer cette action.',
			'resource-exhausted': 'Les ressources sont épuisées. Veuillez réessayer plus tard.',
			'failed-precondition': 'La condition préalable n\'est pas remplie.',
			'aborted': 'L\'opération a été annulée.',
			'out-of-range': 'La valeur est hors limites.',
			'unimplemented': 'Cette fonctionnalité n\'est pas encore implémentée.',
			'internal': 'Une erreur interne s\'est produite. Veuillez réessayer.',
			'unavailable': 'Le service est temporairement indisponible.',
			'data-loss': 'Des données ont été perdues lors de l\'opération.'
		};

		return errorMap[code] || `Erreur Firestore: ${code}`;
	}

	/**
	 * Traduit une erreur Storage en message utilisateur.
	 */
	private translateStorageError(code: string): string {
		const errorMap: Record<string, string> = {
			'storage/unauthorized': 'Vous n\'avez pas la permission d\'accéder à ce fichier.',
			'storage/canceled': 'Le téléchargement a été annulé.',
			'storage/unknown': 'Une erreur inconnue s\'est produite lors du téléchargement.',
			'storage/invalid-format': 'Le format du fichier n\'est pas supporté.',
			'storage/invalid-checksum': 'Le fichier est corrompu.',
			'storage/invalid-event-name': 'Nom d\'événement invalide.',
			'storage/invalid-url': 'URL invalide.',
			'storage/invalid-argument': 'Arguments invalides.',
			'storage/no-default-bucket': 'Aucun bucket par défaut configuré.',
			'storage/cannot-slice-blob': 'Impossible de découper le fichier.',
			'storage/server-file-wrong-size': 'La taille du fichier sur le serveur ne correspond pas.'
		};

		return errorMap[code] || `Erreur Storage: ${code}`;
	}

	/**
	 * Affiche l'erreur à l'utilisateur via ToastService.
	 */
	private showErrorToUser(error: AppError): void {
		this.toastService.showToastError(error.message);
	}

	/**
	 * Log l'erreur dans la console avec plus de détails.
	 */
	private logError(error: AppError): void {
		const logData = {
			type: error.type,
			code: error.code,
			message: error.message,
			originalError: error.originalError
		};

		this.logger.error('[ErrorHandler]', logData);
		this.logger.eventByCategory('client', {
			action: 'error',
			error_type: error.type,
			error_code: error.code ?? 'unknown',
			error_message: error.message
		});

		// En production, vous pourriez envoyer ces erreurs à un service de logging
		// comme Sentry, LogRocket, etc.
	}

	/**
	 * Gère une erreur silencieusement (sans toast).
	 * Utile pour les erreurs non critiques.
	 */
	handleErrorSilently(error: unknown): AppError {
		return this.handleError(error, false, true);
	}

	/**
	 * Gère une erreur sans logging (seulement toast).
	 * Utile pour les erreurs attendues.
	 */
	handleErrorQuietly(error: unknown): AppError {
		return this.handleError(error, true, false);
	}
}

